import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Center, Flex, Skeleton, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getFirstDayOfWeek } from 'clipsal-cortex-utils/src/calculations/date-utils';
import { formatDate } from 'clipsal-cortex-utils/src/formatting/formatting';

import { get } from '../../../common/api/api-helpers';
import { DateRangeType, DateRangeTypePicker } from '../../../common/components/DateRangeTypePicker';
import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT } from '../../../common/constants';
import { useRegionalConfig } from '../../../common/hooks/use-regional-config';
import { trackActivityOpened, trackActivityTimeframeChanged } from '../../../utils/common/analytics';
import { formatDynamicDecimal, getDateRangeTypeSufix } from '../../../utils/common/common-utils';
import {
  trackDynatraceActivityOpened,
  trackDynatraceActivityTimeframeChanged,
} from '../../../utils/native/dynatrace-events';
import { ActivityDatePicker } from '../../activity/common/ActivityDatePicker';
import { useDateRangeOptions } from '../../activity/common/use-date-range-options';
import { getInitialSelectedDates } from '../../activity/utils';
import { selectCurrentDayForSite } from '../../site/siteSlice';
import { selectEvChargerData } from '../dashboard/evChargerSlice';
import { formatSecondsToHHMM, groupByWeeks } from '../ev-charger-helper';
import {
  AggregationType,
  ChargingActivityData,
  ChargingActivityRawData,
  ChargingActivityRecordsRawData,
  ChargingActivitySessionData,
} from '../types';
import { HistoryChart } from './HistoryChart';

export interface ChargingActivityState {
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  isCostViewSelected: boolean;
  isLoaded: boolean;
  hasFetchError: boolean;
}

const INITIAL_SELECTED_PERIODS = (currentDay: Date): Record<DateRangeType, Date> => getInitialSelectedDates(currentDay);

const INITIAL_CHARGING_ACTIVITY_DATA: ChargingActivityData = {
  totalCost: '',
  totalChargeTime: '',
  totalEnergyConsumed: '',
  savings: 0,
  chargingSessions: [],
};

const CHART_VIEW_TOOLBAR_RESERVED_HEIGHT_PX = 100;
const MIN_CHART_HEIGHT_PX = 120;

export function ChargingActivity() {
  const DATE_RANGE_OPTIONS = useDateRangeOptions();
  const { t } = useTranslation();
  const { siteDeviceId } = useSelector(selectEvChargerData);
  const currentDay = useSelector(selectCurrentDayForSite);
  const { weekStartsOn } = useRegionalConfig();
  const dataCache = useRef<Map<string, ChargingActivityData>>(new Map());
  const hasTrackedOpen = useRef(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState<number>();
  const [state, setState] = useState<ChargingActivityState>({
    selectedDateRangeType: DateRangeType.Day,
    rangeTypeToSelectedPeriod: INITIAL_SELECTED_PERIODS(currentDay),
    isCostViewSelected: false,
    isLoaded: false,
    hasFetchError: false,
  });

  const [data, setData] = useState<ChargingActivityData>(INITIAL_CHARGING_ACTIVITY_DATA);

  const { selectedDateRangeType, rangeTypeToSelectedPeriod, isCostViewSelected, isLoaded, hasFetchError } = state;
  const dateRangeSufix = getDateRangeTypeSufix(selectedDateRangeType);

  // Track activity opened once per mount.
  useEffect(() => {
    if (!hasTrackedOpen.current) {
      trackActivityOpened();
      trackDynatraceActivityOpened();
      hasTrackedOpen.current = true;
    }
  }, []);

  useLayoutEffect(() => {
    const element = chartContainerRef.current;
    if (!element) return;

    const updateChartHeight = () => {
      const nextHeight = element.clientHeight;
      setChartHeight((previousHeight) => (previousHeight === nextHeight ? previousHeight : nextHeight));
    };

    updateChartHeight();

    const resizeObserver = new ResizeObserver(updateChartHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const handleSelectChart = (isCostViewSelected: boolean) => {
    setState((prevState) => ({ ...prevState, isCostViewSelected }));
  };

  function handleDrillDown(date: Date) {
    const nextRangeType = (() => {
      switch (selectedDateRangeType) {
        case DateRangeType.Year:
          return DateRangeType.Month;
        case DateRangeType.Month:
          return DateRangeType.Week;
        case DateRangeType.Week:
          return DateRangeType.Day;
        default:
          return null;
      }
    })();

    if (!nextRangeType) return;

    setState((prev) => ({
      ...prev,
      selectedDateRangeType: nextRangeType,
      rangeTypeToSelectedPeriod: {
        ...prev.rangeTypeToSelectedPeriod,
        [nextRangeType]: date,
      },
    }));
  }

  useEffect(() => {
    if (!siteDeviceId) return;

    const currentKey = `${siteDeviceId}-${selectedDateRangeType}-${rangeTypeToSelectedPeriod[
      selectedDateRangeType
    ]?.toISOString()}-${weekStartsOn}`;

    const cachedData = dataCache.current.get(currentKey);
    if (cachedData) {
      setData(cachedData);
      setState((prev) => ({ ...prev, isLoaded: true, hasFetchError: false }));
      return;
    }

    const loadData = async () => {
      setState((prev) => ({ ...prev, isLoaded: false, hasFetchError: false }));

      try {
        const fetchedData = await fetchChargingActivity(
          siteDeviceId,
          selectedDateRangeType,
          rangeTypeToSelectedPeriod,
          weekStartsOn
        );

        setData(fetchedData);
        dataCache.current.set(currentKey, fetchedData);
      } catch (error) {
        setData(INITIAL_CHARGING_ACTIVITY_DATA);
        setState((prev) => ({ ...prev, hasFetchError: true }));
      } finally {
        setState((prev) => ({ ...prev, isLoaded: true }));
      }
    };

    loadData();
  }, [selectedDateRangeType, rangeTypeToSelectedPeriod, siteDeviceId, weekStartsOn]);

  function handleDateRangeTypeChange(newDateRangeType: DateRangeType) {
    trackActivityTimeframeChanged(newDateRangeType);
    trackDynatraceActivityTimeframeChanged(newDateRangeType);
    setState((prevState) => ({
      ...prevState,
      selectedDateRangeType: newDateRangeType,
    }));
  }

  function handleDateChange(newDate: Date | null) {
    const updated = {
      ...rangeTypeToSelectedPeriod,
      [selectedDateRangeType]: newDate,
    };
    setState((prevState) => ({
      ...prevState,
      rangeTypeToSelectedPeriod: updated,
    }));
  }

  return (
    <SlidingAnimationPageBase
      title={t('EVSE.charging activity')}
      backURL="../device-status"
      includeTopNavHamButton={true}
      includeBottomNav
      px={4}
    >
      <Box display="flex" flexDirection="column" h={MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT}>
        {data.savings > 0 && (
          <Center py={2} color="#C3C2C6">
            <Text textAlign="center" fontSize={22} fontWeight={400} data-testid="charging-activity-title">
              {t('EVSE.charging cost message')} {`${data.savings}%`} {dateRangeSufix}
            </Text>
          </Center>
        )}

        <Box>
          <DateRangeTypePicker
            dateRangeOptions={DATE_RANGE_OPTIONS}
            selectedDateRangeType={selectedDateRangeType}
            setSelectedDateRangeType={handleDateRangeTypeChange}
          />
          <ActivityDatePicker
            isLoaded={isLoaded}
            selectedDateRangeType={selectedDateRangeType}
            rangeTypeToSelectedPeriod={rangeTypeToSelectedPeriod}
            onChangeDate={handleDateChange}
          />

          <SEHomeCard borderTopLeftRadius={5} mt={2} py={0} px={2} data-testid="activity-stats-row">
            <Flex align="center" justify="space-between" py={3}>
              <Box textAlign="center" minW={0} overflow="hidden" flex={1}>
                <Text fontSize="xs" textTransform="uppercase" data-testid="total-cost-label">
                  {t('Activity.total cost')} {dateRangeSufix}
                </Text>
                <Skeleton isLoaded={!!isLoaded}>
                  <Text fontSize="sm" fontWeight="bold" noOfLines={1} data-testid="total-cost-value">
                    ${data.totalCost}
                  </Text>
                </Skeleton>
              </Box>
              <Box w="1px" alignSelf="stretch" bg="customGrey.400" mx={2} flexShrink={0} />
              <Box textAlign="center" minW={0} flex={1} overflow="hidden">
                <Text fontSize="xs" textTransform="uppercase">
                  {t('EVSE.energy used')}
                </Text>
                <Skeleton isLoaded={!!isLoaded}>
                  <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                    {data.totalEnergyConsumed}{' '}
                    <Text as="span" fontSize="xs" fontWeight="normal">
                      kWh
                    </Text>
                  </Text>
                </Skeleton>
              </Box>
            </Flex>
          </SEHomeCard>
        </Box>

        <Flex direction={'column'} ref={chartContainerRef} flex={1} minH={0}>
          <HistoryChart
            isLoaded={isLoaded}
            hasFetchError={hasFetchError}
            data={data}
            selectedDateRangeType={selectedDateRangeType}
            rangeTypeToSelectedPeriod={rangeTypeToSelectedPeriod}
            onChartChange={handleSelectChart}
            isCostViewSelected={isCostViewSelected}
            height={
              chartHeight === undefined
                ? undefined
                : Math.max(MIN_CHART_HEIGHT_PX, chartHeight - CHART_VIEW_TOOLBAR_RESERVED_HEIGHT_PX)
            }
            onDrillDown={handleDrillDown}
          />
        </Flex>
      </Box>
    </SlidingAnimationPageBase>
  );
}

/**
 * Private function to convert API response to the format used in the app.
 */
function hasMeaningfulSessionData(session: ChargingActivityRecordsRawData) {
  return Boolean(
    session.usage > 0 ||
      (session.total_cost ?? session.cost ?? 0) > 0 ||
      (session.charge_count ?? 0) > 0 ||
      (session.total_charge_duration ?? 0) > 0 ||
      (session.records?.length ?? 0) > 0
  );
}

function trimDailySessions(sessions: ChargingActivityRecordsRawData[]) {
  const firstMeaningfulIndex = sessions.findIndex(hasMeaningfulSessionData);

  if (firstMeaningfulIndex === -1) return [];

  let lastMeaningfulIndex = firstMeaningfulIndex;

  for (let index = sessions.length - 1; index >= firstMeaningfulIndex; index -= 1) {
    if (hasMeaningfulSessionData(sessions[index])) {
      lastMeaningfulIndex = index;
      break;
    }
  }

  const startIndex = Math.max(0, firstMeaningfulIndex - 1);
  const endIndex = Math.min(sessions.length, lastMeaningfulIndex + 2);

  return sessions.slice(startIndex, endIndex);
}

function serializeChargingActivity(
  apiData: ChargingActivityRawData,
  selectedDateRangeType: DateRangeType,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
): ChargingActivityData {
  let records: ChargingActivityRecordsRawData[];
  if (selectedDateRangeType === DateRangeType.Day) {
    records = trimDailySessions(apiData.records || []);
  } else {
    records = apiData.records || [];
  }

  const totalCost = formatDynamicDecimal(apiData.total_cost);
  const totalChargeTime = formatSecondsToHHMM(apiData.total_charge_duration);
  const totalEnergyConsumed = formatDynamicDecimal(apiData.total_energy);
  const savings = apiData.smart_control_savings;
  const serializedSessions = records.map(
    (session: ChargingActivityRecordsRawData, index: number): ChargingActivitySessionData => ({
      id: index + 1,
      start: session.start,
      end: session.end,
      cost: session.total_cost || session.cost || 0,
      usage: session.usage,
      records: session.records || [],
    })
  );
  const chargingSessions =
    selectedDateRangeType === DateRangeType.Month ? groupByWeeks(serializedSessions, weekStartsOn) : serializedSessions;

  return { totalCost, totalChargeTime, totalEnergyConsumed, savings, chargingSessions };
}

const fetchChargingActivity = async (
  siteDeviceId: string,
  selectedDateRangeType: DateRangeType,
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
) => {
  let aggregationType: AggregationType;
  let startDate: string;
  let endDate: string;

  const startTime = 'T00:00:00.000';
  const endTime = 'T23:59:59.999';
  const selectedDate = new Date(rangeTypeToSelectedPeriod[selectedDateRangeType]);
  const formatedSelectedDate = formatDate(rangeTypeToSelectedPeriod[selectedDateRangeType]);

  if (selectedDateRangeType === DateRangeType.Day) {
    startDate = `${formatedSelectedDate}${startTime}`;
    endDate = `${formatedSelectedDate}${endTime}`;
    aggregationType = AggregationType.Session;
  } else if (selectedDateRangeType === DateRangeType.Week) {
    const firstDayOfWeek = getFirstDayOfWeek(selectedDate, weekStartsOn);
    const mutableFirstDayOfWeek = new Date(firstDayOfWeek);
    const lastDayOfWeek = new Date(mutableFirstDayOfWeek.setDate(mutableFirstDayOfWeek.getDate() + 6));

    startDate = `${format(firstDayOfWeek, 'yyyy-MM-dd')}${startTime}`;
    endDate = `${format(lastDayOfWeek, 'yyyy-MM-dd')}${endTime}`;
    aggregationType = AggregationType.Day;
  } else if (selectedDateRangeType === DateRangeType.Month) {
    const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

    startDate = `${format(firstDayOfMonth, 'yyyy-MM-dd')}${startTime}`;
    endDate = `${format(lastDayOfMonth, 'yyyy-MM-dd')}${endTime}`;
    aggregationType = AggregationType.Day;
  } else {
    startDate = `${selectedDate.getFullYear()}-01-01${startTime}`;
    endDate = `${selectedDate.getFullYear() + 1}-01-01${startTime}`;
    aggregationType = AggregationType.Month;
  }
  const uri =
    `/v1/ev_chargers/${siteDeviceId}/history?start_date=${startDate}&end_date=${endDate}` +
    `&agg_type=${aggregationType.toLocaleLowerCase()}`;

  const rawData = await get<ChargingActivityRawData>(uri);
  const serialized = serializeChargingActivity(rawData, selectedDateRangeType, weekStartsOn);

  return serialized;
};
